"use client";

import { Task } from "@/app/queries";
import { Grid } from "@/components/grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getGridFromObject, getObjectFromGrid } from "@/lib/blaze/physics";
import { LIBRARY, Primitive } from "@/lib/blaze/primitives";
import { _Object } from "@/lib/blaze/reality";
import { PlusIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function Playground({ task }: { task: Task }) {
  if (!task) {
    return <div>No task</div>;
  }

  const [perspective, setPerspective] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  const [program, setProgram] = useState<Primitive[]>([]);

  const example1 = getObjectFromGrid(task.train[0].input);
  const [steps, setSteps] = useState<_Object[]>([example1]);

  useEffect(() => {
    if (program.length === 0) {
      return;
    }
    setSteps((prev) => {
      const lastStep = prev[prev.length - 1];
      const lastPrimitive = program[program.length - 1];
      return [...prev, lastPrimitive.operation(lastStep)];
    });
  }, [program]);

  return (
    <div className="flex flex-col gap-4 h-full">
      <div>
        <div>Options</div>
        <div className="flex flex-row gap-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="3d"
              checked={perspective}
              onCheckedChange={setPerspective}
            />
            <Label htmlFor="3d">3d</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="solution"
              checked={showSolution}
              onCheckedChange={setShowSolution}
            />
            <Label htmlFor="solution">Solution</Label>
          </div>
        </div>
        <div>Program</div>
        <div className="flex flex-row gap-2 items-center">
          {program.map((primitive, index) => (
            <Badge
              variant="outline"
              key={primitive.name}
              className="rounded-full px-3 py-1 h-6 group"
            >
              {primitive.name}
              <button
                className="group-hover:block hidden"
                onClick={() => {
                  setProgram((prev) => prev.filter((_, i) => i !== index));
                }}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-full">
                <PlusIcon className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Primitives</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LIBRARY.primitives.map((primitive) => (
                <DropdownMenuItem
                  key={primitive.name}
                  onSelect={() => {
                    setProgram((prev) => [...prev, primitive]);
                  }}
                >
                  {primitive.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ScrollArea className="h-full">
        <div className="pb-[200px] h-fit">
          <h3 className="text-lg font-bold">Examples</h3>
          <div className="flex flex-col gap-4 pl-16">
            {task?.train.map((example, i) => {
              return (
                <div
                  key={task.id + "-example-" + i}
                  className="flex flex-row gap-2 items-start"
                >
                  <div className="flex flex-col gap-2">
                    Input
                    <Grid data={example.input} perspective={perspective} />
                  </div>
                  {i === 0 && (
                    <div className="flex flex-col gap-2">
                      Steps
                      <div className="flex flex-row gap-2">
                        {steps.length > 1 ? (
                          steps.slice(1).map((step, i) => {
                            return (
                              <Grid
                                key={`step-${i}`}
                                data={getGridFromObject(step)}
                                perspective={perspective}
                              />
                            );
                          })
                        ) : (
                          <div className="text-sm text-muted-foreground">
                            No steps
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    Ouput
                    <Grid data={example.output} perspective={perspective} />
                  </div>
                </div>
              );
            })}
          </div>

          <h3 className="text-lg font-bold">Test</h3>
          <div className="flex flex-col gap-4 pl-16">
            {task?.test.map((test, i) => {
              return (
                <div
                  key={task.id + "-example-" + i}
                  className="flex flex-row gap-2"
                >
                  <Grid data={test.input} perspective={perspective} />
                  {showSolution && (
                    <Grid data={test.output} perspective={perspective} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
